import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { applyPageMeta, resetPageMeta } from '../lib/pageMeta';
import {
  parseBranch,
  rememberBranch,
  readRememberedBranch,
  branchEventValue,
  type RegistrationBranch,
} from '../lib/registrationBranch';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Herkunft aus der Branchen-Landingpage (?branche=…): wird für die sichtbare
  // Einordnung im Formular UND für die Matomo-Zuordnung der Registrierung genutzt.
  const [branch, setBranch] = useState<RegistrationBranch | null>(null);
  const [formData, setFormData] = useState({
    company_name: '',
    name: '',
    email: '',
    password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fromUrl = parseBranch(searchParams.toString());
    if (fromUrl) {
      rememberBranch(fromUrl);
      setBranch(fromUrl);
      return;
    }
    // Kein Parameter in der URL: gemerkte Herkunft gilt weiter (z. B. nach Zwischenklick
    // auf /datenschutz und zurück).
    setBranch(readRememberedBranch());
  }, [searchParams]);

  // Ohne eigenen Kopf trägt /register Titel und canonical der Startseite –
  // für Suchmaschinen sieht die Conversion-Seite damit wie ein Duplikat aus.
  useEffect(() => {
    applyPageMeta({
      title: 'Kostenlos registrieren – DSGVOeasy Free starten',
      description:
        'DSGVOeasy Free: Verarbeitungsverzeichnis anlegen, Branchenvorlage wählen, loslegen. Kostenlos starten, monatlich kündbar, Daten in Deutschland.',
      path: '/register',
      withBrandSuffix: false,
    });
    return resetPageMeta;
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.company_name.trim()) {
      setError('Bitte geben Sie Ihren Firmennamen ein.');
      return;
    }
    if (!formData.name.trim()) {
      setError('Bitte geben Sie Ihren Namen ein.');
      return;
    }
    if (!formData.email.trim()) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    if (formData.password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (!accepted) {
      setError('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.register({ ...formData, marketingAttributionConsent: marketingConsent } as any);
      if (response.success && response.data) {
        // Conversion-Tracking: die abgeschlossene Registrierung ist die eigentliche
        // (saubere) Conversion für FB-Ads/Matomo — der LP-Klick ist nur ein Proxy.
        // Einzige Quelle für Matomo-Ziel 1 "Registrierung".
        const w = window as any;
        w.fbq && w.fbq('track', 'CompleteRegistration');
        w._paq && w._paq.push(['trackGoal', 1]);
        // Zusatz-Dimension der Registrierung: Herkunft von der Branchen-Landingpage.
        // Ohne diesen Event ist in Matomo nicht sichtbar, welche Branchenseite
        // tatsächlich Konten erzeugt (Ziel 1 zählt nur die Summe).
        w._paq && w._paq.push(['trackEvent', 'Registrierung', 'Branche', branchEventValue(branch)]);
        navigate('/dashboard');
      } else {
        setError(response.error || 'Registrierung fehlgeschlagen');
      }
    } catch {
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'72', '72full', Arial, Helvetica, sans-serif", color: '#32363a', minHeight: '100vh' }}>
      {/* Nav */}
      <nav style={{
        background: 'rgba(255,255,255,0.97)',
        borderBottom: '1px solid #e5e5e5',
        backdropFilter: 'blur(8px)',
      }}>
        <div style={{
          maxWidth: '1140px',
          margin: '0 auto',
          padding: '0 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '3.5rem',
        }}>
          <Link to="/login" style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none' }}>
            <img src="/logo.png" alt="DSGVOeasy" style={{ height: '2rem', width: 'auto' }} />
            <span style={{ fontWeight: 700, fontSize: '1.125rem', color: '#1a1d21' }}>DSGVOeasy</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <span style={{ color: '#8c9196', fontSize: '0.875rem' }}>Bereits ein Konto?</span>
            <Link to="/login" onClick={() => window.scrollTo(0, 0)} style={{
              color: '#0070f2',
              fontWeight: 600,
              fontSize: '0.875rem',
              textDecoration: 'none',
              padding: '0.4rem 1rem',
              borderRadius: '0.375rem',
              border: '1.5px solid #0070f2',
            }}>Anmelden</Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="register-layout" style={{
        display: 'flex',
        minHeight: 'calc(100vh - 3.5rem)',
      }}>
        {/* Left Side - Benefits */}
        <div className="register-benefits" style={{
          flex: 1,
          background: 'linear-gradient(160deg, #0054b6 0%, #0070f2 50%, #2196f3 100%)',
          color: 'white',
          padding: '4rem 3rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div className="register-benefits-inner" style={{ maxWidth: '480px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '1rem' }}>
              Starten Sie jetzt mit Ihrem Datenschutz-Management
            </h1>
            <p style={{ fontSize: '1.0625rem', opacity: 0.9, lineHeight: 1.6, marginBottom: '3rem' }}>
              In wenigen Minuten haben Sie Ihr Konto eingerichtet und können mit der
              Dokumentation Ihrer DSGVO-Compliance beginnen. Kostenlos und unverbindlich.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
              {benefits.map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '0.625rem',
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.125rem',
                    flexShrink: 0,
                  }}>{b.icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '0.25rem', fontSize: '0.9375rem' }}>{b.title}</div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.85, lineHeight: 1.5 }}>{b.text}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="register-testimonial" style={{
              marginTop: '3rem',
              padding: '1.25rem',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '0.75rem',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <p style={{ fontSize: '0.875rem', opacity: 0.9, lineHeight: 1.6, margin: 0 }}>
                "DSGVOeasy hat unsere Datenschutz-Dokumentation von Wochen auf Tage reduziert.
                Endlich eine Lösung, die man tatsächlich gerne nutzt."
              </p>
              <p style={{ fontSize: '0.8125rem', opacity: 0.7, marginTop: '0.75rem', marginBottom: 0 }}>
                -- Datenschutzbeauftragter, mittelständisches IT-Unternehmen
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="register-form-side" style={{
          flex: 1,
          background: '#f6f8fb',
          padding: '3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ width: '100%', maxWidth: '440px' }}>
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>
                Kostenloses Konto erstellen
              </h2>
              <p style={{ color: '#6a7178', fontSize: '0.9375rem', lineHeight: 1.5 }}>
                Kein Zahlungsmittel erforderlich. Sofort einsatzbereit.
              </p>
              {branch && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.625rem 0.875rem',
                  borderRadius: '0.5rem',
                  background: '#eaf3ff',
                  border: '1px solid #cfe3ff',
                  color: '#0054b6',
                  fontSize: '0.8125rem',
                  lineHeight: 1.5,
                }}>
                  Branchenvorlagen für <strong>{branch.label}</strong> sind nach dem Login direkt auswählbar.
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  background: '#fef2f2',
                  color: '#bb0000',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  marginBottom: '1.25rem',
                  border: '1px solid #fecaca',
                }}>{error}</div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Firmenname *</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Muster GmbH"
                  disabled={loading}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Ihr vollständiger Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Max Mustermann"
                  disabled={loading}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Geschäftliche E-Mail-Adresse *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ihre.email@firma.de"
                  disabled={loading}
                  style={inputStyle}
                />
              </div>

              <div className="register-pw-row" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Passwort *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mind. 6 Zeichen"
                    disabled={loading}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Passwort wiederholen *</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Passwort bestätigen"
                    disabled={loading}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.625rem',
              }}>
                <input
                  type="checkbox"
                  id="privacy-accept"
                  checked={accepted}
                  onChange={e => setAccepted(e.target.checked)}
                  disabled={loading}
                  style={{ marginTop: '0.25rem', width: '1rem', height: '1rem', accentColor: '#0070f2', cursor: 'pointer' }}
                />
                <label htmlFor="privacy-accept" style={{ fontSize: '0.8125rem', color: '#556170', lineHeight: 1.5, cursor: 'pointer' }}>
                  Ich habe die{' '}
                  <Link to="/datenschutz" target="_blank" style={{ color: '#0070f2', textDecoration: 'none', fontWeight: 600 }}>
                    Datenschutzerklärung
                  </Link>{' '}
                  gelesen und akzeptiere die Verarbeitung meiner Daten gemäß den dort beschriebenen Zwecken.
                </label>
              </div>

              <div style={{
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.625rem',
              }}>
                <input
                  type="checkbox"
                  id="marketing-consent"
                  checked={marketingConsent}
                  onChange={e => setMarketingConsent(e.target.checked)}
                  disabled={loading}
                  style={{ marginTop: '0.25rem', width: '1rem', height: '1rem', accentColor: '#0070f2', cursor: 'pointer' }}
                />
                <label htmlFor="marketing-consent" style={{ fontSize: '0.8125rem', color: '#556170', lineHeight: 1.5, cursor: 'pointer' }}>
                  Ich willige ein, dass DSGVOeasy eine pseudonyme Klick-ID (GCLID) an Google Ireland Ltd. übermittelt,
                  um meine Registrierung der Werbeanzeige zuzuordnen, über die ich auf diese Seite gelangt bin.
                  Es werden <strong>keine</strong> personenbezogenen Daten wie Name, E-Mail oder IP-Adresse an Google übertragen.
                  Diese Einwilligung ist freiwillig und kann jederzeit in meinem Konto widerrufen werden.
                  Ohne Einwilligung ist die Nutzung von DSGVOeasy uneingeschränkt möglich.
                  {' '}
                  <Link to="/datenschutz#ads-attribution" target="_blank" style={{ color: '#0070f2', textDecoration: 'none', fontWeight: 600 }}>
                    Details in der Datenschutzerklärung
                  </Link>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  borderRadius: '0.625rem',
                  border: 'none',
                  background: 'linear-gradient(135deg, #0070f2, #0054b6)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  boxShadow: '0 4px 14px rgba(0,112,242,0.3)',
                  transition: 'opacity 0.2s',
                }}
              >
                {loading ? 'Konto wird erstellt...' : 'Kostenlos starten'}
              </button>

              <p style={{
                textAlign: 'center',
                marginTop: '1.25rem',
                fontSize: '0.8125rem',
                color: '#8c9196',
              }}>
                Bereits ein Konto?{' '}
                <Link to="/login" onClick={() => window.scrollTo(0, 0)} style={{ color: '#0070f2', fontWeight: 600, textDecoration: 'none' }}>
                  Jetzt anmelden
                </Link>
              </p>
            </form>

            {/* Footer Links */}
            <div style={{
              marginTop: '2.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #e5e5e5',
              display: 'flex',
              justifyContent: 'center',
              gap: '1.5rem',
            }}>
              <Link to="/datenschutz" style={{ color: '#8c9196', fontSize: '0.75rem', textDecoration: 'none' }}>Datenschutz</Link>
              <Link to="/impressum" style={{ color: '#8c9196', fontSize: '0.75rem', textDecoration: 'none' }}>Impressum</Link>
              <Link to="/agb" style={{ color: '#8c9196', fontSize: '0.75rem', textDecoration: 'none' }}>AGB</Link>
              <Link to="/widerruf" style={{ color: '#8c9196', fontSize: '0.75rem', textDecoration: 'none' }}>Widerruf</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.375rem',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: '#32363a',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.7rem 0.875rem',
  borderRadius: '0.5rem',
  border: '1.5px solid #d1d5db',
  fontSize: '0.9375rem',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
  background: 'white',
};

const benefits = [
  {
    icon: '\u26A1',
    title: 'Sofort einsatzbereit',
    text: 'Kein langwieriges Setup. Registrieren, Organisation anlegen und direkt mit der Dokumentation starten.',
  },
  {
    icon: '\uD83D\uDCCA',
    title: 'Verarbeitungsverzeichnis in Minuten',
    text: 'Geführte Assistenten und Branchenvorlagen statt leerer Formulare. Art. 30 DSGVO war noch nie so einfach.',
  },
  {
    icon: '\uD83D\uDD10',
    title: 'Alles an einem Ort',
    text: 'VVT, AVV-Verträge, TOM-Dokumentation, Datenschutzhinweise und Betroffenenrechte zentral verwalten.',
  },
  {
    icon: '\uD83D\uDC65',
    title: 'Zusammenarbeit im Team',
    text: 'Rollenbasierte Zugriffskontrolle und Freigabe-Workflows für strukturierte Datenschutz-Prozesse.',
  },
  {
    icon: '\uD83D\uDCC4',
    title: 'Audit-ready auf Knopfdruck',
    text: 'PDF-Exporte, lückenlose Protokollierung und Berichte -- jederzeit prüfungsbereit.',
  },
];
